import { useTranslation } from 'react-i18next'
import { SurfaceCard } from '../../design/SurfaceCard'
import type { MySession, SessionTop } from './useMySession'

/** One wall's share of the visit: its tops, its points and its own board. */
export interface RecapWall {
  /** "Bouldering", "Escaladă". Only shown when the visit used both walls. */
  label: string
  tops: SessionTop[]
  points: number | null
  /** Rank on this wall's board when the visit began and now, when both are known. */
  rankFrom: number | null
  rankTo: number | null
}

interface Props {
  session: MySession
  gymTimezone: string | null
  /** The walls you topped on today. Bouldering and lead are scored on
   * different scales and ranked on separate boards, so they are never added
   * together: two walls get a line each. */
  walls: RecapWall[]
}

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h} h ${m} min` : `${m} min`
}

/**
 * The session you just finished, for the rest of the day: how long, how many
 * tops, and how far it moved you on the board. Tomorrow Home is back to the
 * pass.
 */
export function RecapCard({ session, gymTimezone, walls }: Props) {
  const { t } = useTranslation()
  const visit = session.visit!
  const time = (iso: string) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: gymTimezone ?? undefined,
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))

  const minutes = Math.max(
    1,
    Math.round(
      (new Date(visit.checkedOutAt!).getTime() - new Date(visit.checkedInAt).getTime()) / 60_000,
    ),
  )
  const duration = formatDuration(minutes)
  const count = session.tops.length

  return (
    <SurfaceCard className="flex flex-col gap-3 border-success/30 bg-success/5 p-4">
      <div>
        <p className="eyebrow text-success">
          {t('session.recapEyebrow', {
            from: time(visit.checkedInAt),
            to: time(visit.checkedOutAt!),
          })}
        </p>
        <p className="mt-1 text-lg font-bold leading-snug">
          {count > 0
            ? t('session.recapTitle', { count, duration })
            : t('session.recapNoTops', { duration })}
        </p>
      </div>

      {walls.length === 1 ? (
        <WallStats wall={walls[0]} />
      ) : (
        walls.map((wall) => (
          <div key={wall.label} className="flex flex-col gap-1.5 border-t border-success/20 pt-3">
            <p className="text-sm font-bold">{wall.label}</p>
            <WallStats wall={wall} />
          </div>
        ))
      )}
    </SurfaceCard>
  )
}

function WallStats({ wall }: { wall: RecapWall }) {
  const { t } = useTranslation()
  const flashes = wall.tops.filter((top) => top.flash).length
  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <p className="text-xs text-foreground/50">{t('session.statTops')}</p>
          <p className="text-xl font-extrabold tabular-nums">{wall.tops.length}</p>
        </div>
        <div>
          <p className="text-xs text-foreground/50">{t('session.statFlashes')}</p>
          <p className="text-xl font-extrabold tabular-nums">{flashes}</p>
        </div>
        {wall.points !== null && wall.points > 0 && (
          <div>
            <p className="text-xs text-foreground/50">{t('session.statPoints')}</p>
            <p className="text-xl font-extrabold tabular-nums text-success">
              +{wall.points.toLocaleString()}
            </p>
          </div>
        )}
      </div>
      {wall.rankFrom !== null && wall.rankTo !== null && wall.rankTo < wall.rankFrom && (
        <p className="text-sm">{t('session.rankUp', { from: wall.rankFrom, to: wall.rankTo })}</p>
      )}
    </>
  )
}
